/*
  One way of writing a date or a time, for the whole app.

  Written 5 September 2026, on the owner's decision, for "Settle the settings
  that save and do nothing".

  **The fault.** Site Settings let an administrator choose the site's time zone
  and whether clocks read 1pm or 13:00. Both saved correctly. **Nothing read
  either one.** OnHand printed a date or a time in 54 places and every single one
  used whatever machine the person happened to be sitting at, so two people could
  read two different times off the same submitted count, and an exported file
  could disagree with the screen it came from.

  **There was also no shared way of writing a date**, which is the same problem
  seen from the other side: 54 separate calls cannot be given a setting, because
  there is nowhere to put it.

  **How the setting gets here.** `OrgContext` calls `setSiteTime` whenever the
  site is loaded or switched. These are plain functions rather than a hook
  because most of the 54 places are not React components, and a hook cannot be
  called from a CSV builder.

  **Before a site is loaded, and for anybody signed out, these behave exactly as
  the app did before**: the viewer's own machine. That is deliberate. The sign-in
  screen has no site yet, and a blank date there would be worse than a local one.
*/

let ZONE  = undefined;   // undefined means "the machine this is running on"
let CLOCK = undefined;   // undefined means "whatever that machine prefers"

export function setSiteTime(site) {
  ZONE  = site?.time_zone || undefined;
  CLOCK = site?.clock_format === '24h' ? false
        : site?.clock_format === '12h' ? true
        : undefined;
}

// What the site is set to, for a screen that wants to say so.
export function siteZone() { return ZONE; }

/*
  Everything below takes whatever the database gave us: a timestamp, a plain day
  like 2026-09-18, a Date, or nothing at all.

  **A plain day is handled by hand and not by the date reader.** A browser reads
  `2026-09-18` as midnight in Greenwich, which in Texas is the evening BEFORE, so
  handing it over would print the 17th to everybody who uses OnHand. It is a day,
  and a day has no time in it to convert.
*/
const PLAIN_DAY = /^\d{4}-\d{2}-\d{2}$/;

function asDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  if (typeof value === 'string' && PLAIN_DAY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return { plain: new Date(y, m - 1, d) };
  }
  const d = new Date(value);
  return isNaN(d) ? null : d;
}

function fmt(value, opts) {
  const parsed = asDate(value);
  if (!parsed) return '';
  // A plain day is already the right day. Reading it in a time zone would move
  // it, which is the whole point of keeping the two apart.
  if (parsed.plain) return new Intl.DateTimeFormat(undefined, opts).format(parsed.plain);
  return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: ZONE }).format(parsed);
}

// 05/09/2026, the short form a table wants.
export function day(value) {
  return fmt(value, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

// 5 Sep 2026, for somewhere with room.
export function dayShort(value) {
  return fmt(value, { day: 'numeric', month: 'short', year: 'numeric' });
}

/*
  Sep 5, with no year. For a chip, where the year is nearly always this one and
  the width is the scarce thing. Added 8 September 2026: Run History's
  photographs chip read "PHOTOS DELETED SEP 1, 2026" against a drawing that says
  "PHOTOS DELETED 1 SEP", and the year was pushing the chip into the column
  beside it.
*/
export function dayBrief(value) {
  return fmt(value, { day: 'numeric', month: 'short' });
}

// 5 September 2026, for a sentence.
export function dayLong(value) {
  return fmt(value, { day: 'numeric', month: 'long', year: 'numeric' });
}

/*
  How long ago, in words: "just now", "11 minutes ago", "2 hours ago".

  Added 10 September 2026 for the count sheet somebody else has open, where the
  question a person is actually asking is whether to wait or move on.

  **No zone and no site setting**, deliberately, and this is the one function in
  this file that needs neither: a gap between two moments is the same length of
  time wherever either of them is read. A moment that cannot be read at all
  answers an empty string, so a caller can put its own words there rather than
  printing "NaN minutes ago".
*/
export function ago(value) {
  const then = new Date(value).getTime();
  if (!value || Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins === 1) return '1 minute ago';
  if (mins < 60) return mins + ' minutes ago';
  const hours = Math.floor(mins / 60);
  if (hours === 1) return '1 hour ago';
  if (hours < 24) return hours + ' hours ago';
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : days + ' days ago';
}

// 1:32 pm, or 13:32 if the site says so.
export function clock(value) {
  return fmt(value, { hour: 'numeric', minute: '2-digit', hour12: CLOCK });
}

// 5 Sep, 1:32 pm. The compact form a row in a list wants.
export function dayShortAndClock(value) {
  return fmt(value, {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: CLOCK,
  });
}

// Both in full, which is what an audit entry wants.
export function dayAndClock(value) {
  return fmt(value, {
    year: '2-digit', month: '2-digit', day: '2-digit',
    hour: 'numeric', minute: '2-digit', hour12: CLOCK,
  });
}

/*
  Today, in the SITE's day rather than the machine's.

  A rep in one place and an administrator in another should agree about which
  day it is, and near midnight they would not. Returns `YYYY-MM-DD`, which is
  what the database calls a day.
*/
export function siteToday() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const get = t => parts.find(p => p.type === t)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/*
  The moment a day STARTS in the site's time zone, as a timestamp the database
  can compare with, for a filter that asks for "from this day" or "up to this
  day". Added 18 September 2026 (the audit after the rebrand, issue 23).

  **Why it is needed.** The audit log filtered on `day + 'T00:00:00'`, and a
  time with no zone on it is read by the database as Greenwich. In Texas that
  is 7pm the evening before, so "from the 18th" began on the 17th and "up to
  the 18th" stopped at 7pm on the 18th, and the evening's events went missing.

  **How.** Take midnight in Greenwich for that day, ask how far the site's clock
  is from Greenwich at that moment, and move by that much. Asked twice, because
  the answer can change across the move on a day the clocks go forward or back.
  Before a site is loaded, "the site" is the machine this is running on, as for
  everything else in this file.
*/
function zoneGapMs(instant) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ZONE, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(instant));
  const get = t => Number(parts.find(p => p.type === t)?.value);
  const asIfGreenwich = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asIfGreenwich - Math.floor(instant / 1000) * 1000;
}

export function siteDayStart(plainDay) {
  if (!PLAIN_DAY.test(plainDay || '')) return null;
  const [y, m, d] = plainDay.split('-').map(Number);
  const midnight = Date.UTC(y, m - 1, d);
  let at = midnight - zoneGapMs(midnight);
  at = midnight - zoneGapMs(at);
  return new Date(at).toISOString();
}

// The day after a plain day, as a plain day. For "up to and including this
// day", which is "before the next one starts".
export function nextDay(plainDay) {
  if (!PLAIN_DAY.test(plainDay || '')) return null;
  const [y, m, d] = plainDay.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}
